import { appURL } from '../../services/env'
import { ACCOUNT, COMMUNICATION_SETTINGS, PRICING, UNSUBSCRIBE_PAGE } from '../routes'

export const ApplicationType = {
    APP: 'APP',
    ADDON: 'ADDON',
    ADDIN: 'ADDIN',
    TEAMS: 'TEAMS'
}

export const CONNECTOR_ACQUISITION_CHANNELS = [
    ApplicationType.ADDON,
    ApplicationType.ADDIN,
    'IFRAME',
    'MS-ADDIN'
]

export const DEFAULT_INTL_LANG = 'en'

export const VALID_INTL_LANGUAGES = ['en', 'en-us', 'en-uk', 'pt', 'pt-br', 'es', 'fr']

/**
 * Case-insensitively check whether a locale/language tag is one of VALID_INTL_LANGUAGES.
 *
 * VALID_INTL_LANGUAGES itself is all-lowercase, but `preferredLanguage` can legitimately
 * hold a naturally-cased browser locale tag (e.g. 'pt-BR', 'en-US') — onFinishWelcomeDialog
 * (src/redux/sagas/User/index.js) writes it straight from navigator.language. A raw,
 * case-sensitive `VALID_INTL_LANGUAGES.includes(tag)` silently misses those and falls back
 * to DEFAULT_INTL_LANG, even though the language is genuinely supported.
 *
 * @param {*} lang - the locale/language tag to check, e.g. 'pt-BR'.
 * @returns {boolean} True when `lang` is a string matching a supported language, any case.
 */
export const isValidIntlLanguage = (lang) => (
    typeof lang === 'string' && VALID_INTL_LANGUAGES.includes(lang.toLocaleLowerCase())
)

/**
 * Resolve a locale/language tag to the shipped language it should render as.
 *
 * Looks the tag up verbatim first via isValidIntlLanguage, so every shipped spelling —
 * including the distinct 'pt-br' locale, which must NOT collapse to generic 'pt' — keeps
 * resolving exactly as before. When there is no exact match, falls back to the tag's
 * primary subtag (the part before the first '-'), so a regional variant of a shipped base
 * language (e.g. 'es-MX', 'fr-CA', 'pt-PT') resolves to that base language instead of
 * being coerced to English before lookup. An unrecognised base language (e.g. 'de-DE')
 * still falls back to DEFAULT_INTL_LANG.
 *
 * The app ships no region-specific translations beyond 'pt-br', so this matches on base
 * language rather than hand-maintaining every regional variant in VALID_INTL_LANGUAGES —
 * that list would only drift again the first time a new region shows up.
 *
 * @param {*} lang - the locale/language tag to resolve, e.g. 'es-MX'.
 * @returns {string} The resolved shipped language tag (lowercase), or DEFAULT_INTL_LANG.
 */
export const resolveIntlLanguage = (lang) => {
    if (isValidIntlLanguage(lang)) return lang.toLocaleLowerCase()
    const primarySubtag = typeof lang === 'string' ? lang.toLocaleLowerCase().split('-')[0] : ''
    return isValidIntlLanguage(primarySubtag) ? primarySubtag : DEFAULT_INTL_LANG
}

export const IS_AUTHENTICATING_USER_STORAGE_KEY = 'IS_AUTHENTICATING_USER'
export const HAS_WORKFLOWS_STORAGE_KEY = 'HAS_WORKFLOWS_STORAGE_KEY'
export const ADD_ON_BUILDER_CONTEXT = 'add-on-builder'
export const ACCOUNT_MANAGEMENT_CONTEXT = 'account-management'
export const APPLICATION_STATE_BACKUP = 'APPLICATION_STATE_BACKUP'
export const APPLICATION_STATE = 'APPLICATION_STATE'

// Liveness signal written by the add-on pop-up editor window and watched by the
// sidebar so it can recover when the dialog is closed by any means (including the
// native dialog X, which Google Apps Script cannot detect / report back).
export const POP_UP_EDITOR_HEARTBEAT = 'POP_UP_EDITOR_HEARTBEAT'
export const POP_UP_EDITOR_HEARTBEAT_INTERVAL = 1000
export const POP_UP_EDITOR_HEARTBEAT_STALE_THRESHOLD = 4000
export const POP_UP_EDITOR_CLOSE_WATCHER_POLL_INTERVAL = 2000

export const SHEETGO_SOLUTIONS_URL = 'https://www.sheetgo.com/solutions'
export const SHEETGO_SYMBOL_COLORS = ['#00CC3E', '#F80046', '#FFAB00', '#2979FF']
export const SHEETGO_SUPPORT_MAIL = 'support@sheetgo.com'

export const NON_FULL_VERSION_PATHS = [PRICING, ACCOUNT, UNSUBSCRIBE_PAGE, COMMUNICATION_SETTINGS]

export const PRIVACY_POLICY_URL = 'https://www.sheetgo.com/legal/privacy'
export const APP_SECURITY_URL = 'https://www.sheetgo.com/security'
export const TERMS_OF_USE_URL = 'https://www.sheetgo.com/legal/terms'
export const SECURITY_TERMS_URL = 'https://www.sheetgo.com/security'
export const SUPPORT_URL = 'https://support.sheetgo.com'
export const GOOGLE_WORKSPACE_URL = 'https://workspace.google.com/marketplace/app/sheetgo/94172092257'
export const TRANSACTIONAL_SUPPORT_URL = 'https://www.sheetgo.com/contact-support'
export const HOW_TO_REVIEW_GOOGLE_MARKETPLACE = 'https://support.sheetgo.com/en/articles/8529693-how-to-leave-a-review-for-sheetgo-in-the-google-workspace-marketplace'
export const GOOGLE_SERVICE_AUTHORIZATION_HELP_URL = 'https://support.sheetgo.com/en/articles/8529787-how-to-connect-sheetgo-to-you-cloud'
export const THIRD_PARTY_COOKIES_SUPPORT_URL = 'https://support.sheetgo.com/en/articles/8529787-how-to-turn-on-third-party-cookies'
export const CUSTOM_BRANDING_CDN_PREFIX = 'https://cdn.sheetgo.com/app/customer-brandings/'
export const GENERATE_DOCS_LEARN_URL = 'https://support.sheetgo.com/en/articles/8529703-how-to-generate-documents-with-sheetgo#template'
export const HOW_TO_CONNECT_CLOUD_STORAGE_URL = 'https://support.sheetgo.com/en/articles/8529752-how-to-connect-sheetgo-to-your-cloud-storage'
export const HTTP_REQUEST_REFRESH_TOKEN_DOCUMENTATION_URL = 'https://support.sheetgo.com/en/articles/11036722-set-up-refreshing-tokens'
export const HTTP_REQUEST_PAGINATION_DOCUMENTATION_URL = 'https://support.sheetgo.com/en/articles/11036764-add-pagination-to-get-more-data'
// TODO(SG-14168): replace with the real end-user guide URL once published.
export const WEBHOOK_DESTINATION_DOCUMENTATION_URL = 'https://support.sheetgo.com/en/articles/webhook-destination'
export const AUTOMATION_LIMIT_HELP_URL = 'https://support.sheetgo.com/en/articles/10458577-getting-past-the-limit-of-30-automations'
// SG-11620 LOCK #17 (§0.42.2) — destination for the "Use Automations"
// CTA on the FallthroughBanner + FallthroughDialog. Standalone automations
// app for users who don't want workflows_team paid plans.
export const AUTOMATIONS_APP_URL = 'https://automations.sheetgo.com'
/**
 * Link for registering for a personalized Sheetgo demonstration.
 *
 * Default UTM campaign is `trial-started` and UTM source is `pop-up`. Replace those values if that's not the case.
 *
 * SG-14362 (2026-07-27) — utm_source/utm_medium were reversed (`pop-up`/`sheetgo`
 * instead of `app.sheetgo.com`/`pop-up`), the one constant the 07-17 sweep's
 * attribution fix missed. Corrected to the standard app-origin pattern (same
 * shape as TRIAL_CAMPAIGN_DEMO_URL). Stays on /demo/ — "Schedule a session"
 * matches the demo page's own promise (Tier 2).
 */
export const SHEETGO_DEMO_REGISTRATION_URL = 'https://www.sheetgo.com/demo/?utm_source=app.sheetgo.com&utm_medium=pop-up&utm_campaign=trial-started'
export const AI_ONBOARDING_EXPERTS_SESSION_URL = 'https://www.sheetgo.com/demo/?experts_session_invitation_sent=true&referrer_source=app.sheetgo.com&utm_source=app.sheetgo.com&utm_medium=referral&utm_campaign=Guided-onboarding'
export const TRIAL_EXTENSION_EXPERTS_SESSION_URL = 'https://www.sheetgo.com/demo/?experts_session_invitation_sent=true&referrer_source=app.sheetgo.com&utm_source=app.sheetgo.com&utm_medium=referral&utm_campaign=Extend-trial'
/**
 * Get the template install experts session url
 */
export const TEMPLATES_INSTALL_EXPERTS_SESSION_URL = (templateId) => `https://www.sheetgo.com/demo/?experts_session_invitation_sent=true&referrer_source=app.sheetgo.com&utm_source=app.sheetgo.com&utm_medium=referral&utm_campaign=${templateId}`
/**
 * Contact-sales destination for the Enterprise tile CTA on the V42 pricing page.
 * app-origin UTM convention (same shape as SOLUTION_DEMONSTRATION_LINK). (ref SG-12320)
 *
 * SG-14362 (2026-07-27) — repointed from /demo/ to /contact-sales/: that page
 * no longer redirects (see SALES_SUPPORT_LINK in constants/features.js), and
 * this button's "Contact sales" label already promised it.
 */
export const V42_ENTERPRISE_DEMO_URL = 'https://www.sheetgo.com/contact-sales/?utm_source=app.sheetgo.com&utm_medium=contact-sales&utm_campaign=pricing-enterprise&referrer_source=app.sheetgo.com'
/**
 * Contact-sales destination for the WORKFLOWS tile when an Automations
 * Enterprise customer lands on it. Same page, distinct campaign: from this
 * tile the conversation is a DOWNGRADE (Automations Enterprise -> Workflows),
 * whereas `V42_ENTERPRISE_DEMO_URL` is an upsell. Sales needs to know which
 * conversation they are walking into before they pick up the lead. (ref SG-12320)
 *
 * SG-14362 (2026-07-27) — repointed from /demo/ to /contact-sales/, same reason.
 */
export const V42_WORKFLOWS_SALES_DEMO_URL = 'https://www.sheetgo.com/contact-sales/?utm_source=app.sheetgo.com&utm_medium=contact-sales&utm_campaign=pricing-workflows&referrer_source=app.sheetgo.com'
// SG-14196 (2026-07-18) — the retiring in-app sales form's surfaces now open the
// demo page in a new tab, one campaign per surface (the form stays only for the
// Stripe-Radar verify flow). SG-14362 (2026-07-27) — repointed from /demo/ to
// /contact-sales/: that page no longer redirects, and every one of these
// "Contact/Talk to sales" labels already promised it.
export const V42_TEAM_SALES_DEMO_URL = 'https://www.sheetgo.com/contact-sales/?referrer_source=app.sheetgo.com&utm_source=app.sheetgo.com&utm_medium=contact-sales&utm_campaign=pricing-team-sales'
export const SUBSCRIPTION_SALES_DEMO_URL = 'https://www.sheetgo.com/contact-sales/?referrer_source=app.sheetgo.com&utm_source=app.sheetgo.com&utm_medium=contact-sales&utm_campaign=subscription-sales'
export const BILLING_CARD_SALES_DEMO_URL = 'https://www.sheetgo.com/contact-sales/?referrer_source=app.sheetgo.com&utm_source=app.sheetgo.com&utm_medium=contact-sales&utm_campaign=account-enterprise-sales'
export const USAGE_BAR_SALES_DEMO_URL = 'https://www.sheetgo.com/contact-sales/?referrer_source=app.sheetgo.com&utm_source=app.sheetgo.com&utm_medium=contact-sales&utm_campaign=usage-cap-sales'

export const SHEETGO_SURVEY_URL = 'https://sheetgo.io/bhCXwQC'
export const PAID_USER_CONSULTATION_SESSION_LINK = 'https://calendly.com/sheetgomeeting/sheetgo-1-1?hide_gdpr_banner=1&utm_source=app.sheetgo.com&utm_medium=calendly-dialog&utm_campaign=paid-consultation'
export const CUSTOMER_STORY_CONTACT_LINK = 'https://calendly.com/sheetgomeeting/customer-story-chat?hide_gdpr_banner=1&utm_source=app.sheetgo.com&utm_medium=calendly-dialog&utm_campaign=customer-story-chat'
// Should start on the item 6 of the playlist
export const NEW_UI_PLAYLIST_URL = 'https://www.youtube.com/watch?v=BsRJiuSRE-k&list=PL3zznynD1Fq6kS2l9lJrv6uTxFR5WXqWA&index=6'

export const PARTNER_REF_TRIAL_DAYS_PERIOD = 30
export const LEGACY_TRIAL_DAYS_PERIOD = 7
export const TRIAL_DAYS_PERIOD = 14
export const EXTENDED_TRIAL_DAYS_PERIOD = 7
export const LEGACY_EXTENDED_TRIAL_TOTAL_PERIOD = LEGACY_TRIAL_DAYS_PERIOD + EXTENDED_TRIAL_DAYS_PERIOD
export const EXTENDED_TRIAL_TOTAL_PERIOD = TRIAL_DAYS_PERIOD + EXTENDED_TRIAL_DAYS_PERIOD
export const LEAVE_APPLICATION_LOG_ACTION = 'Leave application'
export const PLANS_STARTING_PRICE = 22

export const communicationSetting = Object.freeze({
    MARKETING_PROMOTIONS: 'marketingPromotions',
    SURVEY_FEEDBACK: 'surveyFeedback'
})
export const ALLOWED_REDIRECT_UTM_MEDIUMS = ['email']
export const ALLOWED_REDIRECT_UTM_CAMPAIGNS = ['workflow_dashboard_share']

export const SIGN_UP_DEMO_OFFER_COUNTRIES = [
    'us', 'ca', 'ie', 'uk', 'au', 'nz',
    'united states', 'canada', 'ireland',
    'united kingdom', 'australia', 'new zealand'
]
export const SIGN_UP_SUPPORTED_LANGUAGES = ['en', 'en-us', 'en-uk', 'en-ie', 'en-au', 'en-ca', 'en-nz']

export const AppConfig = {
    AI_RESOURCE_NAME_SUGGESTION: 'aiResourceNameSuggestions',
    APP_UI_STARTING_DATE: 'webAppUIStartingDate', // Control up to when the switch back button will appear
    APP_UI_OFFER_DATE: 'webAppUIOfferDate', // Control starting from when the switch to button will appear
    // Control where users older than this date will be presented the option to migrate to the new UI
    APP_UI_MIGRATION_OFFER_DATE: 'webAppUIMigrationOfferDate',
    // Control when users matching the above date won't be able to switch back to the old UI anymore
    APP_UI_MIGRATION_END_DATE: 'webAppUIMigrationEndDate',
    CHECKOUT_UI: 'checkoutUI',
    APP_UI: 'webAppUI', // Accept "ab", an integer value, "2023", or "2024"
    FEATURED_TEMPLATES: 'featuredTemplates',
    FEATURED_INTEGRATION_PRESETS: 'featuredIntegrationPresets',
    FEATURED_SUCCESS_STORIES: 'featuredSuccessStories',
    FORMS_API: 'formsApi',
    IMPORT_RANGE: 'importRange',
    ENABLE_POP_UP_EDITOR_UX_MODE_CHANGE: 'enablePopUpEditorUxModeChange',
    ENABLE_ALTERNATIVE_BARCODE_SCANNER: 'enableAlternativeBarcodeScanner',
    SKIP_SIGN_UP_FORM: 'skipSignUpForm'
}

export const CommonUserBrowsers = {
    CHROME: 'Chrome',
    EDGE: 'Edge',
    SAFARI: 'Safari',
    OPERA: 'Opera'
}

export const CommonUserBrowsersSupportedVersions = {
    [CommonUserBrowsers.CHROME]: 114,
    [CommonUserBrowsers.EDGE]: 109,
    [CommonUserBrowsers.SAFARI]: 17,
    [CommonUserBrowsers.OPERA]: 96
}

export const CommonUserBrowsersUpdateRoute = {
    [CommonUserBrowsers.CHROME]: 'https://www.google.com/chrome/update/',
    [CommonUserBrowsers.EDGE]: 'https://support.microsoft.com/en-us/topic/update-to-the-new-microsoft-edge-182d0668-e3f0-49da-b8bb-db5675245dc2#bkmk_download_install',
    [CommonUserBrowsers.SAFARI]: 'https://support.apple.com/en-us/102665',
    [CommonUserBrowsers.OPERA]: 'https://www.opera.com/'
}

export const AddOnPopUpEditorUxModes = {
    AUTO_OPEN: 'auto-open',
    DEFAULT: 'default'
}

export const PREFERRED_VERSION_STORAGE_KEY = 'PREFERRED_UI_VERSION'
export const ENTRY_PAGE_URL_STORAGE_KEY = 'ORIGINAL_ENTRY_PAGE_URL'

/** Default web app sidebar width. It also sets the minimum width before the sidebar collapses. */
export const APP_SIDEBAR_DEFAULT_WIDTH = 240
export const APP_SIDEBAR_MAX_WIDTH = 400
export const SIDEBAR_RESIZE_COMMIT_DEBOUNCE_DELAY = 1500

export const APP_AVAILABLE_FEATURES = {
    Excel: 'excel',
    Folder: 'folder',
    BigQueryDash: 'big-query',
    BigQuery: 'bigquery',
    Pdf: 'pdf',
    Mail: 'mail',
    Docs: 'docs',
    Sheets: 'sheets',
    GoogleSheets: 'google-sheets',
    TSV: 'tsv',
    CSV: 'csv',
    MergeData: 'merge-data',
    MergeFromFolder: 'merge-from-folder',
    MergeFromAFolder: 'merge-from-a-folder',
    FilesInAFolder: 'files-in-a-folder',
    FilesFromAFolder: 'files-from-a-folder',
    Merge: 'merge',
    Split: 'split',
    Forms: 'forms',
    DestinationSingleFile: 'single-file-destination',
    DestinationMultipleFiles: 'multiple-files-destination',
    Filter: 'filter',
    TransferFormatting: 'transfer-formatting',
    FilterByCondition: 'filter-by-condition',
    FilterByQuery: 'filter-by-query',
    FilterByColor: 'filter-by-color',
    AutomaticUpdates: 'automatic-updates',
    Schedule: 'schedule',
    BarcodeScanner: 'barcode-scanner',
    DocsGenerator: 'docs-generator',
    Gmail: 'gmail',
    AutomaticTransfers: 'automatic-transfers',
    Trigger: 'trigger',
    Function: 'function'
}

/** Delay to auto-open a cascading menu when the user keeps focus over the parent option. */
export const CASCADING_MENU_AUTO_OPEN_DELAY = 1250

export const ReleaseDate = Object.freeze({
    MAIN_2024_UI: '2024-06-11T00:00:00',
    MAIN_2024_UI_100_SIGN_UP_ROLLOUT: '2024-10-01T00:00:00',
    SIGN_UP_FORM_2025: '2025-05-06T00:00:00',
    WEBAPP_SIDEBAR_ONBOARDING: '2025-06-12T00:00:00',
    AI_ASSISTANT: '2025-07-01T00:00:00',
    SHARED_WORKFLOWS_APPROVAL: '2025-08-01T00:00:00',
    SIGN_UP_FORM_2025_V2: '2025-11-10T00:00:00'
})

export const WEB_APP_SIDEBAR_HEIGHT_BREAKPOINT = 650
export const WEB_APP_SIDEBAR_WIDTH_COLLAPSE_BREAKPOINT = 1080
export const WEB_APP_SIDEBAR_AUTO_EXPAND_DELAY = 75
export const WEB_APP_SIDEBAR_WF_ITEM_HEIGHT = 42
export const WEB_APP_TOP_BAR_HEIGHT = 52
export const WEB_APP_SIDEBAR_NON_WF_ITEMS_HEIGHT = 300

export const LEGACY_PROVIDERS_QUERY_PARAM = 'legacy-providers'

export const IS_USING_LEGACY_PROVIDERS_STORAGE_KEY = 'isUsingLegacyProviders'

export const YouTubePlayerDefaultParameters = Object.freeze({
    widget_referrer: appURL, // Enables analytics for the video
    modestbranding: 1, // Show a white brand instead of the colorful logo
    cc_load_policy: 1, // Enables CC by default
    cc_lang_pref: 'en', // Required to enable CC
    autoplay: 1,
    origin: appURL,
    rel: 0 // Only show videos from the channel when stop playing
})

export const PAGE_CONTAINERS_VARIANT = {
    DASHBOARD: 'DASHBOARD',
    NOTES_PANEL: 'NOTES_PANEL',
    NOTES: 'NOTES',
    FORMS: 'FORMS'
}

export const PAGE_CONTAINERS_VARIANT_KEY_ID = {
    [PAGE_CONTAINERS_VARIANT.DASHBOARD]: 'dashboard_id',
    [PAGE_CONTAINERS_VARIANT.NOTES_PANEL]: 'notes_id',
    [PAGE_CONTAINERS_VARIANT.NOTES]: 'notes_id',
    [PAGE_CONTAINERS_VARIANT.FORMS]: 'forms_id'
}

export const PAGE_CONTAINERS_VARIANT_LOG_PREFIX = {
    [PAGE_CONTAINERS_VARIANT.DASHBOARD]: 'Workflow canvas > Dashboard >',
    [PAGE_CONTAINERS_VARIANT.NOTES_PANEL]: 'Workflow sidebar > Notes >',
    [PAGE_CONTAINERS_VARIANT.NOTES]: 'Workflow canvas > Notes >',
    [PAGE_CONTAINERS_VARIANT.FORMS]: 'Workflow canvas > Forms >'
}

export const InternalAPPConfig = Object.freeze({
    DISABLE_LEGACY_SIGN_IN_PROVIDERS: 'disableLegacySignInProviders',
    LEGACY_SIGN_IN_PROVIDERS_HIDE_DATE: 'legacySignInProvidersHideDate',
    LEGACY_SIGN_IN_PROVIDERS_DISABLE_DATE: 'legacySignInProvidersDisableDate',
    HJ_ADDON_RECORDING_DISTRIBUTION: 'hjAddonRecordingDistribution',
    HJ_RECORDABLE_WORKFLOW_TEMPLATE_IDS: 'hjRecordableWorkflowTemplateIds'
})
